import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { useToast } from '../../hooks/use-toast';
import PageHero from '../../components/common/PageHero';
import { Plus, Pencil, Trash2, ArrowUp, ArrowDown, Upload, ArrowLeft } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const emptyForm = { id: null, name: '', position: '', display_order: 0, image_url: null };

const CouncilManagement = () => {
  const { toast } = useToast();
  const navigate = useNavigate();
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [file, setFile] = useState(null);
  const [saving, setSaving] = useState(false);
  const fileInputRef = useRef(null);

  const fetchMembers = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/api/council-members`);
      setMembers(data.members || []);
    } catch (e) {
      toast({ title: 'Failed to load council members', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchMembers(); }, []);

  const openCreate = () => {
    setEditing('new');
    setForm({ ...emptyForm, display_order: members.length });
    setFile(null);
  };

  const openEdit = (m) => {
    setEditing(m.id);
    setForm({ id: m.id, name: m.name, position: m.position, display_order: m.display_order, image_url: m.image_url });
    setFile(null);
  };

  const closeDialog = () => {
    setEditing(null);
    setForm(emptyForm);
    setFile(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.position.trim()) {
      toast({ title: 'Name and position are required', variant: 'destructive' });
      return;
    }
    setSaving(true);
    try {
      const fd = new FormData();
      fd.append('name', form.name);
      fd.append('position', form.position);
      fd.append('display_order', String(form.display_order));
      if (file) fd.append('file', file);

      if (editing === 'new') {
        await axios.post(`${API}/api/admin/council-members`, fd);
        toast({ title: 'Council member created' });
      } else {
        await axios.put(`${API}/api/admin/council-members/${editing}`, fd);
        toast({ title: 'Council member updated' });
      }
      await fetchMembers();
      closeDialog();
    } catch (err) {
      const msg = typeof err.response?.data?.detail === 'string' ? err.response.data.detail : 'Save failed';
      toast({ title: msg, variant: 'destructive' });
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (m) => {
    if (!window.confirm(`Archive ${m.name}? They can be restored later.`)) return;
    try {
      await axios.delete(`${API}/api/admin/council-members/${m.id}`);
      toast({ title: 'Archived' });
      await fetchMembers();
    } catch {
      toast({ title: 'Archive failed', variant: 'destructive' });
    }
  };

  const moveOrder = async (m, dir) => {
    const newOrder = m.display_order + dir;
    if (newOrder < 0) return;
    try {
      const fd = new FormData();
      fd.append('display_order', String(newOrder));
      await axios.put(`${API}/api/admin/council-members/${m.id}`, fd);
      await fetchMembers();
    } catch {
      toast({ title: 'Reorder failed', variant: 'destructive' });
    }
  };

  return (
    <div className="min-h-screen bg-gray-50" data-testid="council-management-page">
      <PageHero title="COUNCIL MANAGEMENT" />
      <div className="container mx-auto px-4 py-8">
        <button
          onClick={() => navigate('/admin')}
          className="mb-4 flex items-center gap-2 text-sm text-gray-600 hover:text-[#2c3e7d] transition-colors"
        >
          <ArrowLeft size={16} /> Back to Admin Dashboard
        </button>
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-2xl font-bold text-gray-900">Council & Committee Members</h2>
          <Button onClick={openCreate} className="bg-[#2c3e7d] hover:bg-[#1e2a5a]" data-testid="add-council-member-btn">
            <Plus size={18} className="mr-2" /> Add Member
          </Button>
        </div>

        <Card>
          <CardHeader><CardTitle>{loading ? 'Loading…' : `${members.length} members`}</CardTitle></CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {members.map((m) => (
                <div key={m.id} className="border rounded-lg p-4 bg-white" data-testid={`council-card-${m.id}`}>
                  <div className="w-full h-48 bg-gray-100 rounded mb-3 overflow-hidden">
                    {m.image_url ? (
                      <img src={m.image_url.startsWith('/council/') ? m.image_url : `${API}${m.image_url}`} alt={m.name} className="w-full h-full object-cover" />
                    ) : (
                      <div className="flex items-center justify-center h-full text-gray-400">No image</div>
                    )}
                  </div>
                  <h3 className="font-semibold text-gray-900">{m.name}</h3>
                  <p className="text-sm text-blue-600">{m.position}</p>
                  <p className="text-xs text-gray-500 mt-1">Order: {m.display_order}</p>
                  <div className="flex gap-2 mt-3 flex-wrap">
                    <Button size="sm" variant="outline" onClick={() => moveOrder(m, -1)}><ArrowUp size={14} /></Button>
                    <Button size="sm" variant="outline" onClick={() => moveOrder(m, 1)}><ArrowDown size={14} /></Button>
                    <Button size="sm" variant="outline" onClick={() => openEdit(m)} data-testid={`edit-council-${m.id}`}><Pencil size={14} /></Button>
                    <Button size="sm" variant="outline" onClick={() => handleDelete(m)} className="text-red-600 hover:bg-red-50" data-testid={`delete-council-${m.id}`}><Trash2 size={14} /></Button>
                  </div>
                </div>
              ))}
              {!loading && members.length === 0 && (
                <p className="text-gray-500 col-span-full text-center py-8">No council members yet.</p>
              )}
            </div>
          </CardContent>
        </Card>
      </div>

      <Dialog open={editing !== null} onOpenChange={(o) => !o && closeDialog()}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle>{editing === 'new' ? 'Add Council Member' : 'Edit Council Member'}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <Label>Name *</Label>
              <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required data-testid="council-name-input" />
            </div>
            <div>
              <Label>Position *</Label>
              <Input value={form.position} onChange={(e) => setForm({ ...form, position: e.target.value })} placeholder="e.g. President" required data-testid="council-position-input" />
            </div>
            <div>
              <Label>Display Order</Label>
              <Input type="number" value={form.display_order} onChange={(e) => setForm({ ...form, display_order: parseInt(e.target.value || '0', 10) })} />
            </div>
            <div>
              <Label>Photo {editing === 'new' ? '(optional)' : '(leave blank to keep current)'}</Label>
              <div className="mt-2">
                <input ref={fileInputRef} type="file" accept="image/*" onChange={(e) => setFile(e.target.files[0] || null)} className="hidden" id="council-file" />
                <label htmlFor="council-file" className="flex items-center justify-center w-full px-4 py-3 border-2 border-dashed border-gray-300 rounded-lg cursor-pointer hover:border-blue-500">
                  <Upload size={18} className="mr-2 text-gray-500" />
                  <span className="text-sm text-gray-600">{file ? file.name : 'Choose file'}</span>
                </label>
              </div>
              {form.image_url && !file && (
                <img src={form.image_url.startsWith('/council/') ? form.image_url : `${API}${form.image_url}`} alt="current" className="mt-2 h-24 rounded object-cover" />
              )}
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={closeDialog}>Cancel</Button>
              <Button type="submit" className="bg-[#2c3e7d]" disabled={saving} data-testid="save-council-btn">
                {saving ? 'Saving…' : 'Save'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default CouncilManagement;
